import { createApp } from './main.js';
import sentry from './modules/sentryClient.js';

/**
 * Initializes and mounts the Vue application on the client side.
 * Handles hydration, Pinia state restoration, and registration of custom components.
 */
async function main() {
  const { app, router, pinia } = await createApp();

  if (window.__PINIA_STATE__) { // eslint-disable-line no-underscore-dangle
    pinia.state.value = window.__PINIA_STATE__; // eslint-disable-line no-underscore-dangle
  }

  sentry(app);

  await router.isReady();
  app.mount('#app');

  // Register <g-*> web components after mount so Vue hydrates cleanly. Each
  // <g-card> already has its `card` object set by CoursesCard.vue on mount, so
  // we only seed an empty object on any card still missing data — that keeps
  // Stencil's first render() from crashing on `this.card.imageUrl`.
  document.querySelectorAll('g-card').forEach((el) => {
    if (!el.card || typeof el.card !== 'object') {
      el.card = {}; // eslint-disable-line no-param-reassign
    }
  });

  await import('./modules/registerComponents.js');

  // Reveal the page once the above-the-fold custom elements upgrade, so users
  // never see the pre-upgrade flash. The safety timeout guarantees reveal even
  // if a component fails to register.
  const revealApp = () => {
    document.documentElement.classList.remove('js-loading');
  };

  const criticalTags = ['g-heading', 'g-input', 'g-drop-down', 'g-switch', 'g-card'];
  const whenAllDefined = Promise.all(
    criticalTags.map((tag) => window.customElements.whenDefined(tag)),
  );
  const safetyTimeout = new Promise((resolve) => {
    setTimeout(resolve, 1500);
  });

  Promise.race([whenAllDefined, safetyTimeout]).then(() => {
    window.requestAnimationFrame(revealApp);
  });
}

main();
